import React, { useContext, useState } from "react";
import classNames from "classnames";
import WizardContext from "./WizardContext";
import ConfirmModal from "../modals/ConfirmModal";
import { WIZARD_MESSAGES } from "./constants";

/**
 * WizardViewComponent - The main UI component for the wizard
 * Renders the stepper, current page content, and navigation buttons
 */
function WizardViewComponent(props) {
    const context = useContext(WizardContext);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const handleCloseClick = () => {
        openConfirm({
            title: "Cancel Wizard?",
            message: WIZARD_MESSAGES.CANCEL_WARNING(),
            confirmText: "Discard",
            cancelText: "Keep Working",
            type: "warning",
            onConfirm: () => {
                closeConfirm();
                context.handleClose();
            },
            onClose: closeConfirm,
        });
    };

    const children = Array.isArray(props.children)
        ? props.children
        : [props.children];

    const wizardCard = (
        <div>
            {/* Header */}
            <div className="bg-info text-white p-3 d-flex justify-content-between align-items-center">
                <h4 className="mb-0">{context.title}</h4>
                {context.canClose && (
                    <button
                        type="button"
                        className="wizard-close-btn"
                        onClick={handleCloseClick}
                        aria-label="Close"
                        title="Close wizard"
                    >
                        ×
                    </button>
                )}
            </div>

            {/* Content */}
            <div className="d-flex">
                {/* Stepper - Left side */}
                <div
                    style={{
                        width: "250px",
                        borderRight: "1px solid #dee2e6",
                        padding: "1rem",
                    }}
                >
                    <div className="timeline">
                        {children.map((page, index) => (
                            <div
                                key={index}
                                className={
                                    index === children.length - 1
                                        ? "timeline-item-last"
                                        : ""
                                }
                            >
                                <span
                                    className={classNames("fas", {
                                        "bg-info":
                                            index === context.currentPage,
                                        "bg-white":
                                            index !== context.currentPage,
                                        "border border-info":
                                            index !== context.currentPage,
                                        "text-info":
                                            index < context.currentPage,
                                        [`fa-${
                                            page.props.icon || "circle"
                                        }`]: true,
                                    })}
                                ></span>
                                <div
                                    className={classNames("timeline-item", {
                                        "bg-info":
                                            index === context.currentPage,
                                    })}
                                >
                                    <h3
                                        className={classNames(
                                            "timeline-header",
                                            "text-sm",
                                            {
                                                "text-white":
                                                    index ===
                                                    context.currentPage,
                                                "text-info":
                                                    index ===
                                                    context.currentPage,
                                            }
                                        )}
                                    >
                                        {page.props.title}
                                    </h3>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Page content - Right side */}
                <div className="p-3" style={{ flex: 1 }}>
                    {children.map((component, index) => {
                        return index === context.currentPage ? (
                            <component.type
                                key={index}
                                {...component.props}
                                inputManifest={context.inputManifest}
                                emittedManifest={context.emittedManifest}
                                onNextRequested={
                                    component.props.onNextRequested
                                }
                                onBackRequested={
                                    component.props.onBackRequested
                                }
                                onFinishRequested={
                                    component.props.onFinishRequested
                                }
                            />
                        ) : null;
                    })}
                </div>
            </div>

            {/* Footer */}
            <div className="bg-light p-3 d-flex justify-content-between align-items-center">
                {/* Skip button - Left side */}
                <div>
                    {children[context.currentPage]?.props?.skippable && (
                        <button
                            className="btn btn-sm btn-warning"
                            onClick={context.handleSkip}
                        >
                            Skip
                        </button>
                    )}
                </div>

                {/* Navigation buttons - Right side */}
                <div className="d-flex gap-3">
                    {/* Back button - Hidden on first page */}
                    {context.canBack && (
                        <button
                            className="btn btn-sm btn-outline-info"
                            onClick={context.moveBack}
                        >
                            Back
                        </button>
                    )}

                    {/* Finish or Next button */}
                    {context.canFinish ? (
                        <button
                            className="btn btn-sm btn-info"
                            onClick={context.handleFinish}
                            disabled={context.isFinishing}
                        >
                            {context.isFinishing && (
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                            )}
                            Finish
                        </button>
                    ) : (
                        <button
                            className="btn btn-sm btn-info"
                            onClick={context.moveNext}
                            disabled={!context.canNext}
                        >
                            Next
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    // Render as modal if isModal is true, otherwise render directly
    return (
        <>
            {context.isModal ? (
                <div
                    className="modal fade show"
                    style={{
                        display: "block",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        zIndex: 1050,
                    }}
                    onClick={(e) => {
                        // Close on backdrop click
                        if (e.target === e.currentTarget) {
                            handleCloseClick();
                        }
                    }}
                >
                    <div className="modal-dialog modal-dialog-centered modal-xl">
                        <div
                            className="modal-content"
                            style={{ border: "none" }}
                        >
                            {wizardCard}
                        </div>
                    </div>
                </div>
            ) : (
                wizardCard
            )}

            {/* Cancel Confirmation Modal */}
            {confirmCfg.open && (
                <ConfirmModal
                    open={confirmCfg.open}
                    type={confirmCfg.type}
                    title={confirmCfg.title}
                    message={confirmCfg.message}
                    confirmText={confirmCfg.confirmText}
                    cancelText={confirmCfg.cancelText}
                    onConfirm={confirmCfg.onConfirm}
                    onClose={confirmCfg.onClose}
                    loading={confirmCfg.loading}
                />
            )}
        </>
    );
}

export default WizardViewComponent;
